import { useEffect, useState } from "react";
import { Plus, Tag } from "lucide-react";

const field = "bg-black border border-[#2a2f3d] px-3 py-2 text-sm focus:border-[#e69d27] outline-none";
const PRODUCTS = [["denoise_launch", "Denoise"], ["skin_launch", "Skin"], ["texture_launch", "Texture"], ["reactor_launch", "Reactor"], ["reactor_upgrade", "Reactor upgrade"],
  ["suite_bundle", "Finishing Suite"], ["genalpha_lifetime", "Gen Alpha"], ["recast_lifetime", "Recast"], ["ai_pack", "AI Pack"]];
const EMPTY = { code: "", type: "percent", value: "", max: "", days: "", keys: [] };
const err = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save the code.");

function PromoForm({ admin, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => setF({ ...f, keys: f.keys.includes(k) ? f.keys.filter((x) => x !== k) : [...f.keys, k] });
  const submit = async (e) => {
    e.preventDefault(); setMsg(null);
    const body = { code: f.code.trim(), lookup_keys: f.keys, [f.type === "percent" ? "percent_off" : "amount_off"]: Number(f.value),
      max_redemptions: f.max ? Number(f.max) : null, expires_days: f.days ? Number(f.days) : null };
    try { const { data } = await admin.post("/promos", body); onCreated(data); setF(EMPTY); } catch (x) { setMsg(err(x)); }
  };
  return (
    <form onSubmit={submit} className="border border-[#2a2f3d] bg-[#0e1013] p-4 space-y-3" data-testid="admin-promo-form">
      <div className="flex flex-wrap gap-2 items-center">
        <input className={`${field} w-40 uppercase`} placeholder="CODE" value={f.code} onChange={set("code")} required data-testid="admin-promo-code-input" />
        <select className={`${field} w-28`} value={f.type} onChange={set("type")} data-testid="admin-promo-type-select"><option value="percent">% off</option><option value="amount">€ off</option></select>
        <input className={`${field} w-24`} type="number" min="0" step="0.01" placeholder={f.type === "percent" ? "20" : "15"} value={f.value} onChange={set("value")} required data-testid="admin-promo-value-input" />
        <input className={`${field} w-32`} type="number" min="1" placeholder="max uses" value={f.max} onChange={set("max")} data-testid="admin-promo-max-input" />
        <input className={`${field} w-36`} type="number" min="1" placeholder="valid for (days)" value={f.days} onChange={set("days")} data-testid="admin-promo-days-input" />
        <button className="btn-amber px-4 py-2 text-sm flex items-center gap-2 h-[38px]" data-testid="admin-promo-create-btn"><Plus size={14} /> Create code</button>
      </div>
      <div className="flex flex-wrap gap-2 text-xs text-[#9ba3b4]" data-testid="admin-promo-products">
        <span className="py-1">Only for:</span>
        {PRODUCTS.map(([k, n]) => (
          <button type="button" key={k} onClick={() => toggle(k)} className={`px-2 py-1 border transition-colors ${f.keys.includes(k) ? "border-[#e69d27] text-[#e69d27]" : "border-[#2a2f3d]"}`} data-testid={`admin-promo-product-${k}`}>{n}</button>
        ))}
        <span className="py-1">{f.keys.length ? "" : "(none selected = every product)"}</span>
      </div>
      {msg && <p className="text-sm text-[#ff7a7a]" data-testid="admin-promo-error">{msg}</p>}
    </form>
  );
}

const PromoRow = ({ p, onToggle, busy }) => (
  <tr className="border-t border-[#1d212b]" data-testid={`admin-promo-row-${p.code}`}>
    <td className="py-2 font-mono text-[#e69d27]">{p.code}</td>
    <td>{p.percent_off ? `${p.percent_off}%` : `€${p.amount_off}`}</td>
    <td className="text-[#9ba3b4]">{p.products.length ? p.products.join(", ") : "all products"}</td>
    <td>{p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</td>
    <td className="text-[#9ba3b4]">{p.expires_at ? new Date(p.expires_at * 1000).toLocaleDateString() : "never"}</td>
    <td><button disabled={busy} onClick={() => onToggle(p)} className={`btn-ghost px-2 py-1 text-xs ${p.active ? "" : "text-[#5c6479]"}`} data-testid={`admin-promo-toggle-${p.code}`}>{p.active ? "active · turn off" : "off · turn on"}</button></td>
  </tr>
);

export const AdminPromos = ({ admin }) => {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => { admin.get("/promos").then((r) => setList(r.data)).catch(() => setList([])); }, [admin]);
  const toggle = async (p) => {
    setBusy(p.id); setMsg(null);
    try {
      const { data } = await admin.post(`/promos/${p.id}/active`, { active: !p.active });
      setList((l) => l.map((x) => (x.id === data.id ? data : x)));
    } catch (x) { setMsg(err(x)); }
    setBusy(null);
  };
  return (
    <section className="space-y-3" data-testid="admin-promos">
      <h2 className="font-display uppercase text-2xl flex items-center gap-2"><Tag size={18} className="text-[#e69d27]" /> Promo codes</h2>
      <p className="text-xs text-[#9ba3b4]">Customers enter codes in the payment form on the checkout page. Each code applies once per order (first payment for plans).</p>
      <PromoForm admin={admin} onCreated={(d) => setList((l) => [d, ...l])} />
      <table className="w-full text-sm" data-testid="admin-promo-table">
        <thead className="text-left font-mono text-[11px] uppercase tracking-widest text-[#5c6479]">
          <tr><th className="py-2">Code</th><th>Discount</th><th>Products</th><th>Used</th><th>Expires</th><th /></tr>
        </thead>
        <tbody>{list.map((p) => <PromoRow key={p.id} p={p} onToggle={toggle} busy={busy === p.id} />)}</tbody>
      </table>
      {msg && <p className="text-sm text-[#ff7a7a]" data-testid="admin-promo-toggle-error">{msg}</p>}
    </section>
  );
};
